import { useLocation } from "react-router";
import {
    Sidebar,
    SidebarContent,
    SidebarGroup,
    SidebarGroupLabel,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarMenuSub,
    SidebarMenuSubItem,
} from "@/components/ui/sidebar";
import React from "react";

import { Skeleton } from "@/components/ui/skeleton";
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { ChevronRight } from "@/assets";
import { NewLink } from "@/components/new-link";
import {
    filterAndSortPosts,
    subjects,
    useHelpPostManifest,
} from "./use-help-post";
import { useIsStaff } from "@/hooks/useIsStaff";

export const HelpSidebar = ({
    ref,
    ...props
}: React.ComponentProps<"div"> & {
    side?: "left" | "right";
    variant?: "sidebar" | "floating" | "inset";
    collapsible?: "offcanvas" | "icon" | "none";
}) => {
    const { postManifest, isError } = useHelpPostManifest({ enabled: true });
    const isStaff = useIsStaff();
    const location = useLocation();

    const postBySubject = subjects.map(({ id, label }) => ({
        id,
        label,
        posts: filterAndSortPosts(postManifest, isStaff, id),
    }));

    return (
        <Sidebar {...props} ref={ref}>
            {props.children}
            <SidebarContent>
                <SidebarGroup>
                    <SidebarGroupLabel>Help topics</SidebarGroupLabel>
                    <SidebarMenu>
                        {isError ? (
                            <div>
                                Failed to fetch help topics. Please refresh to
                                try again
                            </div>
                        ) : !postManifest ? (
                            <Skeleton />
                        ) : !postManifest.length ? (
                            <div>No help topics found</div>
                        ) : (
                            postBySubject.map(({ id, label, posts }) =>
                                id === "none" ? (
                                    posts.map((post) => (
                                        <SidebarMenuItem key={post.slug}>
                                            <SidebarMenuButton asChild>
                                                <NewLink
                                                    useNavlink
                                                    className={
                                                        "text-default-color cursor-pointer"
                                                    }
                                                    to={`/help/${post.slug}`}
                                                >
                                                    <span>{post.title}</span>
                                                </NewLink>
                                            </SidebarMenuButton>
                                        </SidebarMenuItem>
                                    ))
                                ) : (
                                    <Collapsible
                                        key={label}
                                        className="group/collapsible"
                                        defaultOpen={posts.some(({ slug }) =>
                                            location.pathname.endsWith(slug),
                                        )}
                                    >
                                        <SidebarMenuItem className="">
                                            <CollapsibleTrigger className="relative w-full cursor-pointer">
                                                <div className="absolute -left-6 flex h-10 items-center">
                                                    <ChevronRight className="transition-transform group-data-[state=open]/collapsible:rotate-90" />
                                                </div>
                                                <SidebarMenuButton asChild>
                                                    <span>{label}</span>
                                                </SidebarMenuButton>
                                            </CollapsibleTrigger>
                                            <CollapsibleContent>
                                                <SidebarMenuSub>
                                                    {posts.map((post) => (
                                                        <SidebarMenuSubItem>
                                                            <SidebarMenuButton
                                                                asChild
                                                            >
                                                                <NewLink
                                                                    useNavlink
                                                                    className={
                                                                        "text-default-color cursor-pointer"
                                                                    }
                                                                    to={`/help/${post.slug}`}
                                                                >
                                                                    <span>
                                                                        {
                                                                            post.title
                                                                        }
                                                                    </span>
                                                                </NewLink>
                                                            </SidebarMenuButton>
                                                        </SidebarMenuSubItem>
                                                    ))}
                                                </SidebarMenuSub>
                                            </CollapsibleContent>
                                        </SidebarMenuItem>
                                    </Collapsible>
                                ),
                            )
                        )}
                    </SidebarMenu>
                </SidebarGroup>
            </SidebarContent>
        </Sidebar>
    );
};
